import { useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, CalendarPlus, FileDiff, MapPin, Settings2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { money, STATUS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ErrorBoundary, ErrorState, Loading } from "@/components/States";
import BoardTab from "@/components/board/BoardTab";
import ClientProject from "@/components/project/ClientProject";
import ExpensesTab from "@/components/project/ExpensesTab";
import { SiteTab } from "@/components/project/SiteCards";
import EstimateTab from "@/components/project/EstimateTab";
import PhotosTab from "@/components/project/PhotosTab";
import ScheduleTab from "@/components/project/ScheduleTab";
import SettingsTab from "@/components/project/SettingsTab";
import WishlistTab from "@/components/project/WishlistTab";
import PreferencesTab from "@/components/project/PreferencesTab";
import OverviewTab from "@/components/project/OverviewTab";
import PrestartTab from "@/components/project/prestart/PrestartTab";
import PlansTab from "@/components/project/plans/PlansTab";
import CostsTab from "@/components/project/CostsTab";
import HandoverTab from "@/components/project/HandoverTab";
import { monthsUntil } from "@/components/project/prestart/meta";
import { ChangeOrderDialog } from "@/components/project/ChangeOrders";
import { SignoffStamp, useSignoffs } from "@/components/project/Signoff";

export const CLUSTERS = [
  ["Build", ["prestart", "plans", "schedule", "site", "photos"]],
  ["Money", ["estimate", "expenses", "costs"]],
  ["Design", ["board", "prefs", "wishlist"]],
  ["Close-out", ["handover"]],
];
export const TAB_LABELS = { prestart: "Pre-Start Planning", plans: "Plans", costs: "Job Costs", board: "Board", prefs: "Design Preferences", wishlist: "Wish List", estimate: "Estimate", photos: "Photos", schedule: "Schedule", expenses: "Expenses", site: "Trades & Site", handover: "Handover", settings: "Settings" };
const STATUS_TONE = { lead: "bg-stone-200 text-stone-700", design: "bg-sky-100 text-sky-800", estimating: "bg-sky-100 text-sky-800", pre_start: "bg-secondary text-foreground", in_progress: "bg-emerald-100 text-emerald-800", active: "bg-emerald-100 text-emerald-800", on_hold: "bg-amber-100 text-amber-800", complete: "bg-primary/10 text-primary" };
const fmt = (iso) => (iso ? new Date(iso + "T12:00:00").toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : null);

function Header({ p, admin, signoffs, go, onChangeOrder, backTo }) {
  const dates = [fmt(p.start_date), fmt(p.end_date)].filter(Boolean).join(" → ");
  const until = p.status === "pre_start" ? monthsUntil(p.start_date) : null;
  return (
    <header className="mb-5 border-b pb-4" data-testid="project-header">
      {p.status === "pre_start" && (
        <button type="button" onClick={() => go("prestart")} className="mb-3 flex w-full flex-wrap items-center justify-between gap-2 rounded-lg border bg-secondary px-3 py-2 text-left text-sm transition-colors hover:bg-secondary/70" data-testid="prestart-strip">
          <span><span className="font-medium">Confirmed — construction {until ? `starts in ${until}` : "starts later"}.</span> <span className="text-muted-foreground">Use this time to settle questions, products and savings in Pre-Start Planning.</span></span>
          <span className="text-xs text-muted-foreground">Open planning →</span>
        </button>
      )}
      <Link to={backTo} className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="back-to-projects-link"><ArrowLeft className="h-3 w-3" />Projects</Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="page-title truncate" data-testid="project-title">{p.name}</h1>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_TONE[p.status] || STATUS_TONE.lead}`} data-testid="project-status-badge">{STATUS[p.status]}</span>
          </div>
          <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground" data-testid="project-meta">
            {p.client_name && <span>{p.client_name}</span>}
            {p.address && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{p.address}</span>}
            {dates && <span>{dates}</span>}
            {admin && p.budget ? <span>Budget {money(p.budget)}</span> : null}
          </p>
          <SignoffStamp list={signoffs} kind="preferences" className="mt-2" /><SignoffStamp list={signoffs} kind="proposal" className="mt-1" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => go("schedule")} data-testid="header-add-schedule"><CalendarPlus className="mr-1.5 h-4 w-4" />Schedule</Button>
          {admin && <Button size="sm" variant="outline" className="rounded-full" onClick={onChangeOrder} data-testid="header-change-order"><FileDiff className="mr-1.5 h-4 w-4" />Change order</Button>}
          {admin && <Button size="sm" variant="ghost" className="rounded-full" onClick={() => go("settings")} aria-label="Project settings" data-testid="header-settings"><Settings2 className="h-4 w-4" /></Button>}
        </div>
      </div>
    </header>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const { user, workspace } = useAuth();
  const [params, setParams] = useSearchParams();
  const { data: p, setData, error, loading, reload } = useFetch(`/projects/${id}`);
  const admin = user.role === "admin";
  const client = user.role === "client";
  const signoffs = useSignoffs(client ? null : id);
  const [co, setCo] = useState(false);
  const saved = workspace?.settings?.tab_order || [];
  const rank = (k) => { const i = saved.indexOf(k); return i < 0 ? (k === "prestart" ? -1 : k === "plans" ? -0.5 : 99) : i; };
  const cv = { photos: true, schedule: true, ...(p?.client_view || {}) };
  const hidden = workspace?.hidden || [];
  const allowed = (k) => !hidden.includes(k) && (admin ? true : user.role === "crew" ? !["estimate", "settings", "costs", "handover"].includes(k) : ["prestart", "plans", "board", "prefs", "wishlist", "photos", "schedule"].includes(k) && cv[k] !== false);
  const groups = CLUSTERS.map(([name, keys]) => [name, [...keys].filter(allowed).sort((a, b) => rank(a) - rank(b))]).filter(([, keys]) => keys.length);
  const tab = params.get("tab") || (p?.status === "pre_start" && !client ? "prestart" : "overview");
  const go = (k) => `/projects/${id}?tab=${k}`;
  const open = (k) => setParams({ tab: k });

  if (loading && !p) return <div className="p-6 md:p-8"><Loading rows={4} /></div>;
  if (error) return <div className="p-6 md:p-8"><ErrorState message={error} onRetry={reload} /></div>;

  if (client && tab === "overview") {
    return (
      <div className="p-4 md:p-8">
        <Link to="/" className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="back-to-projects-link"><ArrowLeft className="h-3 w-3" />Back</Link>
        <ClientProject project={p} onOpenBoard={() => open("board")} />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <Header p={p} admin={admin} signoffs={signoffs.data || []} go={open} onChangeOrder={() => setCo(true)} backTo={client ? "/" : "/projects"} />
      <Tabs value={tab} onValueChange={open}>
        <div className="-mx-4 mb-5 overflow-x-auto px-4 md:mx-0 md:px-0">
          <TabsList className="h-auto w-max min-w-full justify-start gap-1 bg-transparent p-0" data-testid="project-tabs">
            <TabsTrigger value="overview" data-testid="project-tab-overview" className="tab-trigger">Overview</TabsTrigger>
            {groups.map(([name, keys]) => (
              <div key={name} className="flex items-end gap-1 border-l pl-3 ml-2" data-testid={`tab-group-${name.toLowerCase()}`}>
                <span className="mb-2 hidden text-[10px] font-semibold uppercase tracking-wider text-muted-foreground md:inline">{name}</span>
                {keys.map((k) => <TabsTrigger key={k} value={k} data-testid={`project-tab-${k}`} className="tab-trigger">{TAB_LABELS[k]}</TabsTrigger>)}
              </div>
            ))}
            {tab === "settings" && admin && <TabsTrigger value="settings" data-testid="project-tab-settings" className="tab-trigger ml-2 border-l pl-3">Settings</TabsTrigger>}
          </TabsList>
        </div>
        <ErrorBoundary>
          <TabsContent value="overview"><OverviewTab projectId={id} go={go} /></TabsContent>
          <TabsContent value="board"><BoardTab projectId={id} /></TabsContent>
          <TabsContent value="prefs"><PreferencesTab projectId={id} /></TabsContent>
          <TabsContent value="wishlist"><WishlistTab projectId={id} /></TabsContent>
          {admin && <TabsContent value="estimate"><EstimateTab projectId={id} /></TabsContent>}
          <TabsContent value="photos"><PhotosTab projectId={id} /></TabsContent>
          <TabsContent value="prestart"><PrestartTab projectId={id} /></TabsContent>
          <TabsContent value="plans"><PlansTab projectId={id} /></TabsContent>
          <TabsContent value="costs"><CostsTab projectId={id} /></TabsContent>
          {admin && <TabsContent value="handover"><HandoverTab projectId={id} /></TabsContent>}
          <TabsContent value="schedule"><ScheduleTab projectId={id} /></TabsContent>
          {!client && <TabsContent value="expenses"><ExpensesTab projectId={id} /></TabsContent>}
          {!client && <TabsContent value="site"><SiteTab project={p} /></TabsContent>}
          {admin && <TabsContent value="settings"><SettingsTab project={p} onSaved={setData} reload={reload} /></TabsContent>}
        </ErrorBoundary>
      </Tabs>
      {admin && <ChangeOrderDialog open={co} onOpenChange={setCo} projectId={id} source="project" />}
    </div>
  );
}
